<template>
  <Modal
    ref="changeApproveStatusModalRef"
    @onNegativeActionClick="closeChangeApproveStatusModal"
    @onPositiveActionClick="changeProductsApproveStatus"
    @modalClosed="modalClosed"
    :isPositiveActionPending="isChangeApproveStatusLoading"
    :isPositiveActionButtonDisabled="isChangeApproveStatusLoading || !selectedModalStatus || !isStatusChanged"
    negativeActionText="Vazgeç"
    positiveActionText="Kaydet"
    :modalStyle="{ width: '460px' }"
    :showIcon="false"
    class="changeApproveStatusModal"
  >
    <template v-slot:body>
      <h2 class="changeApproveStatusModal-title">Onay Durumunu Değiştir</h2>
      <RadioButtonGroup
        name="changeApproveStatusModal-radioGroup"
        class="changeApproveStatusModal-radioGroup"
        :options="productStatusOptions"
        v-model="selectedModalStatus"
        @select="selectedModalStatus = $event"
      />
    </template>
  </Modal>
</template>

<script setup>
import { ref, computed } from "vue";
import { useStore } from "vuex";
import { Modal, RadioButtonGroup } from "@n11/so-ui-components";
import { sendProductsChangeApprovalStatusEvent, sendProductDetailEvent } from "@/utils/ga4/helper";
import { GA4_EVENT } from "@/utils/ga4/event";
import { ToastMessageTypes } from "@/utils/enum/toastMessageTypes";

const emit = defineEmits(["statusUpdated"]);

const props = defineProps({
  productIds: {
    type: Array,
    required: false,
    default: () => [],
  },
  productStatus: {
    type: String,
    required: false,
    default: "",
  },
});

const store = useStore();

const selectedModalStatus = ref();
const changeApproveStatusModalRef = ref();
const isChangeApproveStatusLoading = ref(false);
const isStatusUpdateSuccessful = ref(false);
const isDismissEventSent = ref(false);

const productStatusOptions = computed(() => [
  { id: "Active", name: "Onaylı", selected: props.productStatus === "Active" },
  { id: "Suspended", name: "Beklemede", selected: props.productStatus === "Suspended" },
]);

const isStatusChanged = computed(() => {
  if (!selectedModalStatus.value) return false;
  return selectedModalStatus.value.id !== props.productStatus;
});

const openChangeApproveStatusModal = () =>
  changeApproveStatusModalRef?.value?.openModal();

const closeChangeApproveStatusModal = () => {
  if (!isStatusUpdateSuccessful.value) {
    sendDismissEvent();
    isDismissEventSent.value = true;
  }
  changeApproveStatusModalRef?.value?.hiddenModal();
};

const modalClosed = () => {
  if (!isStatusUpdateSuccessful.value && !isDismissEventSent.value) {
    sendDismissEvent();
  }
  isStatusUpdateSuccessful.value = false;
  isDismissEventSent.value = false;
  selectedModalStatus.value = null;
};

const sendDismissEvent = () => {
  const productIdsArray = Array.isArray(props.productIds) ? props.productIds : [props.productIds];
  const itemId = productIdsArray[0] || productIdsArray;
  const eventName = props.productStatus === 'Active'
    ? GA4_EVENT.PUT_UP_FOR_SALE_DISMISS
    : props.productStatus === 'Suspended'
      ? GA4_EVENT.PUT_ON_HOLD_DISMISS
      : null;

  if (eventName) {
    sendProductDetailEvent(eventName, {
      button_name: 'Vazgeç',
      button_type: 'Dismiss',
      button_area: 'Product and Sales',
      item_id: itemId,
      status: props.productStatus,
    });
  }
};

const changeProductsApproveStatus = async () => {
  const productIdsArray = Array.isArray(props.productIds) ? props.productIds : [props.productIds];
  
  const payload = {
    productIds: productIdsArray,
    productStatus: selectedModalStatus.value.id,
  };
  
  isChangeApproveStatusLoading.value = true;

  try {
    await store.dispatch("products/changeProductsApproveStatus", payload);
    
    emit("statusUpdated");
    
    store.commit("common/SET_TOASTER_DATA", {
      type: ToastMessageTypes.SUCCESS,
      description: "Ürün durumu başarıyla güncellendi.",
    });
    
    sendProductsChangeApprovalStatusEvent({
      approval_status: selectedModalStatus.value.id,
      quantity: productIdsArray.length,
    });

    isStatusUpdateSuccessful.value = true;

    const itemId = productIdsArray[0] || productIdsArray;
    const successEventName = selectedModalStatus.value.id === 'Active'
      ? GA4_EVENT.PUT_UP_FOR_SALE_SUCCESS
      : GA4_EVENT.PUT_ON_HOLD_SUCCESS;

    sendProductDetailEvent(successEventName, {
      button_name: 'Kaydet',
      button_type: 'Success',
      button_area: 'Product and Sales',
      item_id: itemId,
      status: props.productStatus === 'Active' ? 'Suspended' : 'Active',
    });
  } catch (error) {
    console.error(error);
    store.commit("common/SET_TOASTER_DATA", {
      type: ToastMessageTypes.ERROR,
      title: ToastMessageTypes.ERROR_TITLE,
      description: ToastMessageTypes.ERROR_APPROVAL_STATUS_DESCRIPTION,
    });
  } finally {
    closeChangeApproveStatusModal();
    isChangeApproveStatusLoading.value = false;
  }
};

defineExpose({
  openChangeApproveStatusModal,
});
</script>

<style lang="scss" scoped>
.changeApproveStatusModal {
  &-title {
    font-size: 20px;
    font-weight: 700;
    text-align: left;
    margin-bottom: 12px;
  }

  &-radioGroup {
    margin-bottom: 12px;
    gap: 16px;
  }
}
</style>
